import React, { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { fmtEUR, fmtDate } from "../../lib/format";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Plus, Trash2, CheckCircle2, Undo2, Save, Receipt } from "lucide-react";
import { toast } from "sonner";

const TIPO_LABEL = { caparra: "Caparra (proposta / opzione)", acconto: "Acconto al preliminare", saldo: "Saldo al rogito" };
const DEFAULT_ROWS = [
  { tipo: "caparra", descrizione: "", importo_previsto: "", data_prevista: "" },
  { tipo: "acconto", descrizione: "", importo_previsto: "", data_prevista: "" },
  { tipo: "saldo", descrizione: "", importo_previsto: "", data_prevista: "", importo_manuale: false },
];

export default function PianoIncassiPanel({ unitId, canWrite, onChanged, onFattura }) {
  const [p, setP] = useState(null);
  const [rows, setRows] = useState(null);
  const [prezzo, setPrezzo] = useState("");
  const [inc, setInc] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/units/${unitId}/piano-incassi`).then((r) => {
    setP(r.data); setPrezzo(r.data.prezzo || "");
    setRows(r.data.righe.length ? r.data.righe : DEFAULT_ROWS.map((x) => ({ ...x })));
  }).catch((e) => toast.error(fmtErr(e.response?.data?.detail)));
  useEffect(() => { load(); }, [unitId]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/units/${unitId}/piano-incassi`, { righe: rows.filter((x) => x.tipo === "saldo" || x.importo_previsto || x.data_prevista), prezzo_vendita: prezzo === "" ? null : parseFloat(prezzo) });
      setP(r.data); setRows(r.data.righe); toast.success("Piano incassi salvato"); onChanged?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const confirmIncasso = async () => {
    try {
      const r = await api.post(`/units/${unitId}/piano-incassi/${inc.id}/incassa`, { data: inc.data, importo: inc.importo === "" ? null : parseFloat(inc.importo) });
      setP(r.data); setRows(r.data.righe); setInc(null); toast.success("Incasso registrato: movimento creato"); onChanged?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const storna = async (r) => {
    try { const res = await api.post(`/units/${unitId}/piano-incassi/${r.id}/storna`); setP(res.data); setRows(res.data.righe); toast.success("Incasso annullato"); onChanged?.(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const upd = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v, ...(k === "importo_previsto" && r.tipo === "saldo" ? { importo_manuale: v !== "" } : {}) } : r)));

  if (!p || !rows) return <div className="text-muted-foreground">Caricamento piano incassi...</div>;
  const prezzoNum = parseFloat(prezzo) || 0;
  const altri = rows.filter((r) => r.tipo !== "saldo").reduce((a, r) => a + (parseFloat(r.importo_previsto) || 0), 0);
  const saldoAuto = Math.max(0, prezzoNum - altri);
  const pct = p.previsto ? Math.min(100, Math.round((p.incassato / p.previsto) * 100)) : 0;

  return (
    <div className="space-y-4" data-testid="piano-incassi">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-3"><div className="text-xs uppercase text-muted-foreground">Prezzo di vendita</div>
          {canWrite ? <Input type="number" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} className="mt-1 h-9 font-semibold" data-testid="pi-prezzo" /> : <div className="text-xl font-bold kpi-num">{fmtEUR(p.prezzo)}</div>}</Card>
        <Card className="p-3"><div className="text-xs uppercase text-muted-foreground">Previsto</div><div className="text-xl font-bold kpi-num" data-testid="pi-previsto">{fmtEUR(p.previsto)}</div></Card>
        <Card className="p-3"><div className="text-xs uppercase text-muted-foreground">Incassato</div><div className="text-xl font-bold kpi-num text-emerald-600" data-testid="pi-incassato">{fmtEUR(p.incassato)}</div></Card>
        <Card className="p-3"><div className="text-xs uppercase text-muted-foreground">Da incassare</div><div className="text-xl font-bold kpi-num text-amber-600" data-testid="pi-da-incassare">{fmtEUR(p.da_incassare)}</div>
          {p.prossima && <div className="text-xs text-muted-foreground">Prossimo: {p.prossima.tipo} {fmtEUR(p.prossima.importo)}{p.prossima.data ? ` il ${fmtDate(p.prossima.data)}` : ""}</div>}</Card>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden" data-testid="pi-progress"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr><th className="text-left p-3">Voce</th><th className="text-right p-3">Importo previsto</th><th className="text-left p-3">Data prevista</th><th className="text-right p-3">Incassato</th><th className="text-left p-3">Data incasso</th><th className="p-3 w-40" /></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const locked = !!r.movement_id;
              return (
                <tr key={r.id || i} className="border-t" data-testid={`pi-row-${r.tipo}`}>
                  <td className="p-3">
                    <div className="font-medium">{TIPO_LABEL[r.tipo]}</div>
                    {canWrite && !locked ? <Input value={r.descrizione || ""} placeholder="nota (facoltativa)" onChange={(e) => upd(i, "descrizione", e.target.value)} className="h-7 mt-1 text-xs" /> : r.descrizione && <div className="text-xs text-muted-foreground">{r.descrizione}</div>}
                  </td>
                  <td className="p-3 text-right">
                    {canWrite && !locked ? (
                      <div>
                        <Input type="number" value={r.tipo === "saldo" && !r.importo_manuale ? saldoAuto : (r.importo_previsto ?? "")} onChange={(e) => upd(i, "importo_previsto", e.target.value)} className="h-8 text-right w-36 ml-auto" data-testid={`pi-importo-${r.tipo}`} />
                        {r.tipo === "saldo" && <button type="button" className="text-[11px] text-muted-foreground underline mt-1" onClick={() => upd(i, "importo_manuale", !r.importo_manuale)}>{r.importo_manuale ? "torna al calcolo automatico" : "calcolato: prezzo − caparra − acconti"}</button>}
                      </div>
                    ) : <span className="kpi-num font-semibold">{fmtEUR(r.importo_previsto)}</span>}
                  </td>
                  <td className="p-3">{canWrite && !locked ? <Input type="date" value={r.data_prevista || ""} onChange={(e) => upd(i, "data_prevista", e.target.value)} className="h-8 w-40" data-testid={`pi-data-${r.tipo}`} /> : (r.data_prevista ? fmtDate(r.data_prevista) : "—")}</td>
                  <td className="p-3 text-right kpi-num">{r.importo_incassato ? <span className="text-emerald-600 font-semibold">{fmtEUR(r.importo_incassato)}</span> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3">{r.data_incasso ? fmtDate(r.data_incasso) : <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3 text-right">
                    {locked ? (
                      <div className="flex items-center justify-end gap-2"><Badge className="bg-emerald-600">Incassato</Badge>{canWrite && <Button size="icon" variant="ghost" title="Annulla incasso" onClick={() => storna(r)} data-testid={`pi-storna-${r.tipo}`}><Undo2 className="w-4 h-4" /></Button>}</div>
                    ) : canWrite && r.id ? (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => setInc({ id: r.id, tipo: r.tipo, importo: r.residuo || "", data: new Date().toISOString().slice(0, 10) })} disabled={!(r.residuo > 0)} data-testid={`pi-incassa-${r.tipo}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Segna incassato</Button>
                        <Button size="sm" variant="ghost" title="Emetti fattura per questa voce" onClick={() => onFattura?.(r.id)} data-testid={`pi-fattura-${r.tipo}`}><Receipt className="w-4 h-4" /></Button>
                        {r.tipo !== "saldo" && <Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                      </div>
                    ) : canWrite && r.tipo !== "saldo" ? <Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-destructive" /></Button> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {canWrite && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button variant="outline" size="sm" onClick={() => setRows([...rows.filter((r) => r.tipo !== "saldo"), { tipo: "acconto", descrizione: "", importo_previsto: "", data_prevista: "" }, ...rows.filter((r) => r.tipo === "saldo")])} data-testid="pi-add-acconto"><Plus className="w-4 h-4 mr-1" /> Aggiungi acconto</Button>
          <Button onClick={save} disabled={saving} data-testid="pi-save"><Save className="w-4 h-4 mr-1" /> Salva piano</Button>
        </div>
      )}
      <p className="text-xs text-muted-foreground">«Segna incassato» crea il movimento in entrata sull'operazione (conto bancario da abbinare in riconciliazione) e aggiorna lo stato dell'unità: caparra → prenotata, acconto → preliminare definitivo, saldo → rogitata.</p>

      <Dialog open={!!inc} onOpenChange={(o) => !o && setInc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registra incasso — {inc && TIPO_LABEL[inc.tipo]}</DialogTitle></DialogHeader>
          {inc && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Importo incassato €</Label><Input type="number" value={inc.importo} onChange={(e) => setInc({ ...inc, importo: e.target.value })} data-testid="pi-inc-importo" /></div>
              <div><Label className="text-xs">Data incasso</Label><Input type="date" value={inc.data} onChange={(e) => setInc({ ...inc, data: e.target.value })} data-testid="pi-inc-data" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setInc(null)}>Annulla</Button><Button onClick={confirmIncasso} data-testid="pi-inc-confirm">Conferma incasso</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
